'use client';
import React, { useEffect, useState, useMemo } from 'react';
import { DirectoryProvider } from '@/components/directory/DirectoryContext';
import { useDynamicAirtableRecords } from '@/lib/airtable/client-hooks';
import { parseAttachments } from '@/lib/airtable/transform';
import Link from 'next/link';
import { FolderOpen } from 'lucide-react';
import { LoadingPattern } from "@/components/ui/pattern-viewer";
import CompanyCard from './CompanyCard';
import FeaturedCarousel from './FeaturedCarousel';
import { extractCompanyFields, CompanyFields } from './extractCompanyFields';
import ProviderFilterBar from './ProviderFilterBar';
import ProviderActiveFilters from './ProviderActiveFilters';
import ProviderDirectorySection from './ProviderDirectorySection';
import { Breadcrumb } from '@/components/ui/breadcrumb';

function getDisplayValue(obj: any): string {
  if (!obj || typeof obj !== 'object') return '';
  if (obj.Name) return obj.Name;
  if (obj.name) return obj.name;
  const firstString = Object.values(obj).find(v => typeof v === 'string');
  if (firstString) return String(firstString);
  if (obj.id) return obj.id;
  return '[object]';
}

export default function ProvidersPage() {
  // Fetch all companies
  const { data, isLoading, isError } = useDynamicAirtableRecords('PROVIDERS');
  // Fetch schema for dynamic field detection
  const [schema, setSchema] = useState<any>(null);
  const [schemaLoading, setSchemaLoading] = useState(true);

  useEffect(() => {
    setSchemaLoading(true);
    fetch('/api/airtable/schema')
      .then(res => res.json())
      .then(data => setSchema(data))
      .catch(() => setSchema(null))
      .finally(() => setSchemaLoading(false));
  }, []);

  // Find the schema for the current table
  const currentTableSchema = useMemo(
    () => schema?.tables?.find((t: any) => t.name === 'Providers' || t.id === 'PROVIDERS'),
    [schema]
  );



  // Add state for filters
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [selectedTechnologies, setSelectedTechnologies] = useState<string[]>([]);
  const [selectedProductTypes, setSelectedProductTypes] = useState<string[]>([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);

  // Fetch all Technology records for the filter dropdown
  const { data: technologyRecords, isLoading: techLoading } = useDynamicAirtableRecords('TECHNOLOGY');
  const technologyLookup = useMemo(() => {
    if (!technologyRecords || !Array.isArray(technologyRecords)) return {};
    const map: Record<string, string> = {};
    technologyRecords.forEach((rec: any) => {
      const name = rec['Technology Name'] || rec['Name'] || rec.id;
      map[rec.id] = name;
    });
    return map;
  }, [technologyRecords]);

  // Fetch all Product Types records for the filter dropdown
  const { data: productTypeRecords, isLoading: productTypeLoading } = useDynamicAirtableRecords('PRODUCT_TYPES');

  // Fetch all Platform records for the filter dropdown
  const { data: platformRecords, isLoading: platformLoading } = useDynamicAirtableRecords('PLATFORMS');
  const platformLookup = useMemo(() => {
    if (!platformRecords || !Array.isArray(platformRecords)) return {};
    const map: Record<string, string> = {};
    platformRecords.forEach((rec: any) => {
      const name = rec['Platform Name'] || rec['Name'] || rec.id;
      map[rec.id] = name;
    });
    return map;
  }, [platformRecords]);

  // Extract unique countries from Providers data
  const countryOptions = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    const countries = new Set<string>();
    data.forEach((provider: any) => {
      const countryField = provider['Country'];
      if (Array.isArray(countryField)) {
        countryField.forEach((c) => { if (c) countries.add(c); });
      } else if (typeof countryField === 'string' && countryField) {
        countries.add(countryField);
      }
    });
    return Array.from(countries).sort((a, b) => a.localeCompare(b));
  }, [data]);

  // Extract unique technologies from Providers data using the lookup
  const technologyOptions = useMemo(() => {
    if (!data || !Array.isArray(data) || !technologyLookup) return [];
    const technologies = new Set<string>();
    
    console.log('Technology lookup:', technologyLookup);
    console.log('Providers data sample:', data.slice(0, 2));
    
    // Log all available fields from the first provider
    if (data.length > 0) {
      console.log('Available fields in providers:', Object.keys(data[0]));
    }
    
    data.forEach((provider: any) => {
      // Try different possible field names for technologies
      const technologyField = provider['Technologies (new)'] || provider['Technologies'] || provider['Technology'] || provider['Tech'];
      console.log('Provider:', provider['Company'] || provider['Name'], 'Technology field:', technologyField);
      
      if (Array.isArray(technologyField)) {
        technologyField.forEach((tech) => { 
          if (tech) {
            // Handle both string values and object references
            const techId = typeof tech === 'string' ? tech : (tech.id || tech);
            console.log('Tech ID:', techId, 'Tech Name:', technologyLookup[techId]);
            if (techId) technologies.add(techId);
          }
        });
      } else if (technologyField) {
        // Handle single value
        const techId = typeof technologyField === 'string' ? technologyField : (technologyField.id || technologyField);
        console.log('Single tech ID:', techId, 'Tech Name:', technologyLookup[techId]);
        if (techId) technologies.add(techId);
      }
    });
    
    console.log('Found technologies:', Array.from(technologies));
    
    return Array.from(technologies).map(techId => ({
      id: techId,
      name: technologyLookup[techId] || techId
    })).sort((a, b) => a.name.localeCompare(b.name));
  }, [data, technologyLookup]);

  // Extract unique product types from Providers data
  const productTypeOptions = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    const productTypes = new Set<string>();
    data.forEach((provider: any) => {
      const productTypeField = provider['Product Types'];
      if (Array.isArray(productTypeField)) {
        productTypeField.forEach((type) => { 
          if (type) {
            // Handle both string values and object references
            const typeId = typeof type === 'string' ? type : (type.id || type);
            // Filter out 'Consumables'
            if (typeId !== 'Consumables') {
              productTypes.add(typeId);
            }
          }
        });
      } else if (productTypeField) {
        // Handle single value
        const typeId = typeof productTypeField === 'string' ? productTypeField : (productTypeField.id || productTypeField);
        // Filter out 'Consumables'
        if (typeId !== 'Consumables') {
          productTypes.add(typeId);
        }
      }
    });
    return Array.from(productTypes).map(typeId => ({
      id: typeId,
      name: typeId // For now, use the ID as name since we don't have a lookup table
    })).sort((a, b) => a.name.localeCompare(b.name));
  }, [data]);

  // Extract unique platforms from Providers data using the new Platforms lookup field
  const platformOptions = useMemo(() => {
    if (!data || !Array.isArray(data) || !platformLookup) return [];
    const platforms = new Set<string>();
    
    data.forEach((provider: any) => {
      // Use the new Platforms lookup field
      const platformField = provider['Platforms'];
      if (Array.isArray(platformField)) {
        platformField.forEach((platform) => { 
          if (platform) {
            // Handle both string values and object references
            const platformId = typeof platform === 'string' ? platform : (platform.id || platform);
            if (platformId) platforms.add(platformId);
          }
        });
      } else if (platformField) {
        // Handle single value
        const platformId = typeof platformField === 'string' ? platformField : (platformField.id || platformField);
        if (platformId) platforms.add(platformId);
      }
    });
    
    return Array.from(platforms).map(platformId => ({
      id: platformId,
      name: platformLookup[platformId] || platformId
    })).sort((a, b) => a.name.localeCompare(b.name));
  }, [data, platformLookup]);

  // Add state for view mode (card or list)
  const [viewMode, setViewMode] = useState<'card' | 'list'>('card');
  
  // Add state for sorting
  const [sortBy, setSortBy] = useState<string>('name-asc');

  // Loading/Error states
  if (isLoading || schemaLoading) {
    return (
      <DirectoryProvider>
        <div className="flex min-h-[60vh] items-center justify-center">
          <LoadingPattern patternName="cell-pattern" size="md" />
        </div>
      </DirectoryProvider>
    );
  }
  if (isError) {
    return (
      <DirectoryProvider>
        <div className="text-center py-20 text-red-500">Error loading providers.</div>
      </DirectoryProvider>
    );
  }
  if (!data || !Array.isArray(data) || !currentTableSchema) {
    return (
      <DirectoryProvider>
        <div className="text-center py-20">No providers found.</div>
      </DirectoryProvider>
    );
  }

  // Sort and filter providers
  const filteredAndSortedProviders = Array.isArray(data) ? [...data].filter((provider: any) => {
    const { name } = extractCompanyFields(provider, currentTableSchema);
    
    // Country filter logic
    if (selectedCountries.length > 0) {
      const countryField = provider['Country'];
      if (Array.isArray(countryField)) {
        if (!countryField.some((c) => selectedCountries.includes(c))) return false;
      } else if (typeof countryField === 'string') {
        if (!selectedCountries.includes(countryField)) return false;
      } else {
        return false;
      }
    }
    
    // Technology filter logic
    if (selectedTechnologies.length > 0) {
      const techField = provider['Technologies (new)'] || provider['Technologies'] || provider['Technology'] || provider['Tech'];
      if (Array.isArray(techField)) {
        const providerTechIds = techField.map((tech) => {
          const techId = typeof tech === 'string' ? tech : (tech.id || tech);
          return techId;
        });
        if (!providerTechIds.some((techId) => selectedTechnologies.includes(techId))) return false;
      } else if (techField) {
        const techId = typeof techField === 'string' ? techField : (techField.id || techField);
        if (!selectedTechnologies.includes(techId)) return false;
      } else {
        return false;
      }
    }
    
    // Product Type filter logic
    if (selectedProductTypes.length > 0) {
      const productTypeField = provider['Product Types'];
      if (Array.isArray(productTypeField)) {
        const providerProductTypeIds = productTypeField.map((type) => {
          const typeId = typeof type === 'string' ? type : (type.id || type);
          return typeId;
        });
        if (!providerProductTypeIds.some((typeId) => selectedProductTypes.includes(typeId))) return false;
      } else if (productTypeField) {
        const typeId = typeof productTypeField === 'string' ? productTypeField : (productTypeField.id || productTypeField);
        if (!selectedProductTypes.includes(typeId)) return false;
      } else {
        return false;
      }
    }
    
    // Platform filter logic
    if (selectedPlatforms.length > 0) {
      const platformField = provider['Platforms'];
      if (Array.isArray(platformField)) {
        const providerPlatformIds = platformField.map((platform) => {
          const platformId = typeof platform === 'string' ? platform : (platform.id || platform);
          return platformId;
        });
        if (!providerPlatformIds.some((platformId) => selectedPlatforms.includes(platformId))) return false;
      } else if (platformField) {
        const platformId = typeof platformField === 'string' ? platformField : (platformField.id || platformField);
        if (!selectedPlatforms.includes(platformId)) return false;
      } else {
        return false;
      }
    }
    
    return !!name && name.trim() !== '';
  }).sort((a: any, b: any) => {
    const { name: nameA, location: locationA } = extractCompanyFields(a, currentTableSchema);
    const { name: nameB, location: locationB } = extractCompanyFields(b, currentTableSchema);
    
    switch (sortBy) {
      case 'name-asc':
        return nameA.localeCompare(nameB);
      case 'name-desc':
        return nameB.localeCompare(nameA);
      case 'location-asc':
        return (locationA || '').localeCompare(locationB || '');
      case 'location-desc':
        return (locationB || '').localeCompare(locationA || '');
      default:
        return nameA.localeCompare(nameB);
    }
  }) : [];

  return (
    <DirectoryProvider>
      <div className="min-h-screen bg-neutral-50">
        {/* Hero Header */}
                  <div className="w-full h-64 md:h-80 bg-gradient-to-r from-primary-500 to-accent-500 shadow-lg overflow-hidden relative rounded-none">
          <div className="max-w-7xl mx-auto w-full h-full flex flex-col md:flex-row md:items-center px-6">
            <div className="w-full">
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                                  <span className="text-xl md:text-2xl lg:text-3xl font-medium flex items-center gap-2">
                    <img 
                      src="/LabAtlas-Logo-White-Full.png" 
                      alt="LabAtlas Logo" 
                      className="h-6 w-6 md:h-8 md:w-8 object-contain"
                    />
                    LabAtlas
                  </span>
                Spatial Biology Providers
              </h1>
              <p className="text-xl md:text-2xl text-white/90 leading-relaxed">
                Discover and compare spatial biology technology providers with LabAtlas' searchable directory. Filter by location, technology, and product types to quickly find partners that match your project needs.
              </p>
              </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Breadcrumb */}
          <div className="mb-6">
            <Breadcrumb 
              items={[
                { label: 'Providers', isCurrentPage: true }
              ]} 
            />
          </div>
          
          {/* Filter Bar */}
          <ProviderFilterBar
            countryOptions={countryOptions}
            technologyOptions={technologyOptions}
            productTypeOptions={productTypeOptions}
            platformOptions={platformOptions}
            selectedCountries={selectedCountries}
            setSelectedCountries={setSelectedCountries}
            selectedTechnologies={selectedTechnologies}
            setSelectedTechnologies={setSelectedTechnologies}
            selectedProductTypes={selectedProductTypes}
            setSelectedProductTypes={setSelectedProductTypes}
            selectedPlatforms={selectedPlatforms}
            setSelectedPlatforms={setSelectedPlatforms}
            techLoading={techLoading}
            productTypeLoading={productTypeLoading}
            platformLoading={platformLoading}
          />
          {/* Active Filter Pills */}
          <ProviderActiveFilters
            selectedCountries={selectedCountries}
            setSelectedCountries={setSelectedCountries}
            selectedTechnologies={selectedTechnologies}
            setSelectedTechnologies={setSelectedTechnologies}
            selectedProductTypes={selectedProductTypes}
            setSelectedProductTypes={setSelectedProductTypes}
            selectedPlatforms={selectedPlatforms}
            setSelectedPlatforms={setSelectedPlatforms}
            technologyOptions={technologyOptions}
            productTypeOptions={productTypeOptions}
            platformOptions={platformOptions}
            onClearFilters={() => {
              setSelectedCountries([]);
              setSelectedTechnologies([]);
              setSelectedProductTypes([]);
              setSelectedPlatforms([]);
            }}
            viewMode={viewMode}
            setViewMode={setViewMode}
            sortBy={sortBy}
            setSortBy={setSortBy}
          />
          
          {/* Providers Display */}
          <ProviderDirectorySection
            filteredProviders={filteredAndSortedProviders}
            viewMode={viewMode}
            extractFields={(provider) => extractCompanyFields(provider, currentTableSchema)}
            technologyOptions={technologyOptions}
            productTypeOptions={productTypeOptions}
            platformOptions={platformOptions}
            selectedTechnologies={selectedTechnologies}
            selectedProductTypes={selectedProductTypes}
            selectedPlatforms={selectedPlatforms}
            selectedCountries={selectedCountries}
          />
        </div>
      </div>
      <footer className="w-full py-8 bg-neutral-900">
        <div className="max-w-7xl mx-auto px-4 text-center text-neutral-500 text-sm">
          © 2025 LabAtlas. All rights reserved.
        </div>
      </footer>
    </DirectoryProvider>
  );
} 